import { Button, Grid, TextField, Box, Typography, Checkbox, FormControlLabel, Paper, Fade, Divider, CircularProgress, FormHelperText } from "@mui/material";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { sendOtp, verifyOtpLogin } from "../State/Auth/Action";
import { Email, Lock, Close } from "@mui/icons-material";
import { MuiTelInput } from "mui-tel-input";
import countryData from '../../config/country-phone-code-and-length.json';

const SignInModalContent = ({ handleClose, switchToRegister }) => {
  const dispatch = useDispatch();
  const { auth } = useSelector((store) => store);

  const [formData, setFormData] = useState({
    email: "",
    phone: "",
    otp: "",
  });

  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [phone, setPhone] = useState("");
  const [localPhone, setLocalPhone] = useState("");
  const [isAgreeTC, setIsAgreeTC] = useState(false);

  const handlePhoneChange = (newValue, info) => {
    // ✅ Extract only digits
    setPhone(newValue);
    const cleanedNumber = newValue.replace(/\D/g, "");

    // ✅ Find country rules from JSON
    const countryRule = countryData.find(c => c.code === info.countryCode);
    const countryCode = info.countryCallingCode || "";

    // ✅ Remove country code from cleanedNumber to get local number
    const localNumber = cleanedNumber.startsWith(countryCode)
      ? cleanedNumber.slice(countryCode.length)
      : cleanedNumber;

    // ✅ Construct full number (+countryCode + localNumber)
    const fullNumber = `+${countryCode} ${localNumber}`;

    setLocalPhone(localNumber)

    // ✅ Determine required phone length (local number length only)
    let requiredLength = null;

    if (countryRule && localNumber.trim()) {
      if (countryRule.phoneLength) {
        requiredLength = countryRule.phoneLength; // number or array
      } else if (countryRule.min && countryRule.max) {
        requiredLength = { min: countryRule.min, max: countryRule.max };
      }
    }

    // ✅ Validate local number
    let isValid = true;

    if (Array.isArray(requiredLength)) {
      isValid = requiredLength.includes(localNumber.length);
    } else if (typeof requiredLength === "number") {
      isValid = localNumber.length === requiredLength;
    } else if (requiredLength?.min && requiredLength?.max) {
      isValid = localNumber.length >= requiredLength.min && localNumber.length <= requiredLength.max;
    }

    if (!isValid) {
      setPhoneError(
        `Phone number must be ${Array.isArray(requiredLength)
          ? requiredLength.join(" or ")
          : requiredLength?.min
            ? `${requiredLength.min}-${requiredLength.max}`
            : requiredLength
        } digits`
      );
    } else {
      setPhoneError("");
      // ✅ Send full number to backend
      setFormData(prev => ({ ...prev, phone: fullNumber }));
    }

  };

  useEffect(() => {
    if (auth.user?.role === "CUSTOMER") {
      window.location.reload();
      handleClose?.();
      setOtpSent(false)
    }
  }, [auth.user, handleClose]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "email" && value && !/^\S+@\S+\.\S+$/.test(value)) {
      setError("Invalid email");
    } else {
      setError("");
    }
    setFormData({ ...formData, [name]: value });
  };

  const handleSendOtp = () => {
    const identifier = formData.email || formData.phone;
    if (!isAgreeTC) return;
    // Manually set otpSent to true to show OTP field
    dispatch(sendOtp(identifier, setOtpSent));
  };

  const handleVerifyOtp = () => {
    const identifier = formData.email || formData.phone;
    const otpValue = formData.otp;
    if (!otpValue || otpValue.length !== 4) {
      toast.error("Enter a valid 4-digit OTP");
      return;
    }
    dispatch(verifyOtpLogin(identifier, otpValue, setOtpSent));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!otpSent) {
      handleSendOtp();
    } else {
      handleVerifyOtp();
    }
  };

  const handleCloseClick = () => {
    if (handleClose) {
      handleClose();
      setOtpSent(false)
      setFormData({ email: "", phone: "", otp: "" });
    }
  };

  return (
    <Fade in timeout={600}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minHeight: { xs: '15vh', sm: '20vh' },
          bgcolor: '#fff',
          pt: { xs: 2, sm: 3 },
          pb: { xs: 2, sm: 3 },
          px: { xs: 1, sm: 0 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: { xs: '95%', sm: '400px', md: '450px' },
            maxWidth: '95vw',
            maxHeight: '90vh',
            overflow: 'auto',
            bgcolor: 'white',
            borderRadius: { xs: '16px', sm: '20px' },
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
            border: '1px solid #f1f5f9',
            background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
            position: 'relative'
          }}
        >
          {/* Close Button */}
          <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 9999 }}>
            <Button
              onClick={handleCloseClick}
              sx={{
                minWidth: 'auto',
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: '#ef4444', // Temporary red background for debugging
                color: 'white',
                border: '1px solid #dc2626',
                '&:hover': {
                  bgcolor: '#dc2626',
                  color: 'white',
                  transform: 'scale(1.05)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                },
                transition: 'all 0.2s ease-in-out',
                zIndex: 9999,
                '&:active': {
                  transform: 'scale(0.95)',
                }
              }}
            >
              <Close sx={{ fontSize: '1rem' }} />
            </Button>
          </Box>

          {/* Header Section */}
          <Box sx={{
            width: '100%',
            p: 4,
            textAlign: 'center',
            bgcolor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Decorative Elements */}
            <Box sx={{
              position: 'absolute',
              top: -20,
              right: -20,
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'rgba(55, 65, 81, 0.05)',
              zIndex: 1,
              pointerEvents: 'none'
            }} />
            <Box sx={{
              position: 'absolute',
              bottom: -30,
              left: -30,
              width: 60,
              height: 60,
              borderRadius: '50%',
              bgcolor: 'rgba(55, 65, 81, 0.03)',
              zIndex: 1,
              pointerEvents: 'none'
            }} />

            <Typography
              variant="h5"
              sx={{
                fontWeight: '800',
                color: '#374151',
                textTransform: 'uppercase',
                fontSize: '1.375rem',
                letterSpacing: '1.5px',
                mb: 1,
                position: 'relative',
                zIndex: 2
              }}
            >
              Inarifashion
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#6b7280',
                fontSize: '0.875rem',
                fontWeight: '500',
                position: 'relative',
                zIndex: 2
              }}
            >
              Premium Fashion & Lifestyle
            </Typography>
          </Box>

          {/* Login Form Section */}
          <Box sx={{ p: { xs: 3, sm: 4 } }}>
            <Typography
              variant="h6"
              sx={{
                mb: { xs: 1.5, sm: 2 },
                fontWeight: '700',
                textAlign: 'center',
                color: '#1f2937',
                fontSize: { xs: '1.125rem', sm: '1.25rem' }
              }}
            >
              Login or Sign Up
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mb: { xs: 3, sm: 4 },
                textAlign: 'center',
                color: '#6b7280',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                lineHeight: 1.5
              }}
            >
              Enter your details to continue
            </Typography>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3} direction="column">
                {/* Email Input */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="email"
                    name="email"
                    label="Email Address"
                    type="email"
                    variant="outlined"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={localPhone || otpSent}
                    error={!!error}
                    helperText={error}
                    placeholder="Enter your email"
                    InputProps={{
                      startAdornment: <Email sx={{ mr: 1.5, color: '#9ca3af', fontSize: '1.25rem' }} />,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        fontSize: '0.875rem',
                        bgcolor: '#fafafa',
                        '&:hover': {
                          bgcolor: '#f8fafc',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#d1d5db',
                            borderWidth: '2px',
                          },
                        },
                        '&.Mui-focused': {
                          bgcolor: '#ffffff',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#374151',
                            borderWidth: '2px',
                          },
                        },
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.875rem',
                        color: '#6b7280',
                        fontWeight: '500',
                      },
                      '& .MuiFormHelperText-root': {
                        fontSize: '0.75rem',
                        color: '#ef4444',
                      },
                    }}
                  />
                </Grid>

                {/* OR Divider */}
                <Grid item xs={12} sx={{ textAlign: 'center', my: 1 }}>
                  <Divider sx={{ position: 'relative' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#9ca3af',
                        fontWeight: '700',
                        fontSize: '0.75rem',
                        backgroundColor: 'white',
                        px: 3,
                        textTransform: 'uppercase',
                        letterSpacing: '1px'
                      }}
                    >
                      OR
                    </Typography>
                  </Divider>
                </Grid>

                {/* Phone Input */}
                <Grid item xs={12}>
                  <MuiTelInput
                    fullWidth
                    id="phoneNumber"
                    name="phone"
                    label="Phone Number"
                    variant="outlined"
                    value={phone}
                    onChange={handlePhoneChange}
                    disabled={formData.email || otpSent}
                    placeholder="Enter your phone number"
                    defaultCountry="IN"
                    forceCallingCode
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        fontSize: '0.875rem',
                        bgcolor: '#fafafa',
                        '&:hover': {
                          bgcolor: '#f8fafc',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#d1d5db',
                            borderWidth: '2px',
                          },
                        },
                        '&.Mui-focused': {
                          bgcolor: '#ffffff',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#374151',
                            borderWidth: '2px',
                          },
                        },
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.875rem',
                        color: '#6b7280',
                        fontWeight: '500',
                      },
                    }}
                  />
                  {phoneError && (
                    <FormHelperText error>{phoneError}</FormHelperText>
                  )}
                </Grid>

                {/* OTP Input */}
                {otpSent && (
                  <>
                    {/* Success Message */}
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          bgcolor: '#10b981',
                          color: 'white',
                          p: { xs: 1.5, sm: 2 },
                          borderRadius: { xs: '6px', sm: '8px' },
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        }}
                      >
                        <span>✓</span>
                        OTP sent to {formData.phone || formData.email}
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        id="otp"
                        name="otp"
                        label="OTP"
                        type="text"
                        variant="outlined"
                        value={formData.otp}
                        onChange={handleChange}
                        placeholder="Enter 4-digit OTP"
                        inputProps={{ maxLength: 4 }}
                        InputProps={{
                          startAdornment: <Lock sx={{ mr: { xs: 1, sm: 1.5 }, color: '#9ca3af', fontSize: { xs: '1.125rem', sm: '1.25rem' } }} />,
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: { xs: '8px', sm: '12px' },
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            bgcolor: '#fafafa',
                            '&:hover': {
                              bgcolor: '#f8fafc',
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#d1d5db',
                                borderWidth: '2px',
                              },
                            },
                            '&.Mui-focused': {
                              bgcolor: '#ffffff',
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#374151',
                                borderWidth: '2px',
                              },
                            },
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            color: '#6b7280',
                            fontWeight: '500',
                          },
                        }}
                      />
                    </Grid>
                  </>
                )}

                {/* Terms Checkbox */}
                <Grid item xs={12}>
                  {!otpSent && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          onClick={() => setIsAgreeTC(!isAgreeTC)}
                          value={isAgreeTC}
                          name="agreeTerms"
                          sx={{
                            color: '#374151',
                            '&.Mui-checked': {
                              color: '#374151',
                            },
                            '& .MuiSvgIcon-root': {
                              fontSize: '1.25rem',
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.75rem',
                            color: '#6b7280',
                            lineHeight: 1.5
                          }}
                        >
                          By continuing, I agree to the{' '}
                          <a href="#" style={{ color: '#374151', textDecoration: 'none', fontWeight: '700' }}>
                            Terms of Use
                          </a>{' '}
                          &{' '}
                          <a href="#" style={{ color: '#374151', textDecoration: 'none', fontWeight: '700' }}>
                            Privacy Policy
                          </a>{' '}
                          and I am above 18 years old.
                        </Typography>
                      }
                      sx={{ mt: 0, mb: 3, alignItems: 'flex-start' }}
                    />
                  )}

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    fullWidth
                    disabled={auth?.isLoading || error || phoneError || !isAgreeTC ||
                      (!formData.email?.trim() && (!formData.phone?.trim() || !phone?.trim() || !localPhone?.trim())) ||
                      (otpSent ? formData.otp?.length !== 4 : false)}
                    variant="contained"
                    sx={{
                      py: { xs: 1.5, sm: 2 },
                      bgcolor: '#374151',
                      borderRadius: { xs: '8px', sm: '12px' },
                      '&:hover': {
                        bgcolor: '#1f2937',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(55, 65, 81, 0.4)',
                      },
                      color: '#fff',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      letterSpacing: { xs: '0.5px', sm: '1px' },
                      transition: 'all 0.3s ease-in-out',
                      boxShadow: '0 4px 15px rgba(55, 65, 81, 0.3)',
                    }}
                  >
                    {auth?.isLoading ? (
                      <CircularProgress
                        size={20}
                        sx={{
                          color: "white", // optional: match your theme
                        }}
                      />
                    ) : otpSent ? "Verify OTP" : "Continue"}
                  </Button>
                </Grid>
              </Grid>
            </form>

            {/* Help Link */}
            <Typography
              variant="caption"
              sx={{
                mt: { xs: 1.5, sm: 2 },
                textAlign: 'center',
                display: 'block',
                fontSize: { xs: '0.625rem', sm: '0.75rem' },
                color: '#9ca3af'
              }}
            >
              Having trouble logging in?{' '}
              <a
                href="#"
                style={{
                  color: '#374151',
                  textDecoration: 'none',
                  fontWeight: '700',
                  borderBottom: '1px solid #d1d5db',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderBottomColor: '#374151',
                  }
                }}
              >
                Get help
              </a>
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Fade>
  );
};

export default SignInModalContent; 